import { differenceWith, find, includes, isEqual, omit, property } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { AVERAGE_DAYS_OF_A_YEAR, REDUX_STORE } from '@funnels/constants';
import {
  Asset,
  AssetLiabilityRelation,
  CreateLiabilityAction,
  CustomerData,
  DeleteLiabilityAction,
  FetchLiabilitiesAction,
  Liability,
  ReduxStoreData,
  ServerLiabilityUpdate,
  StoreApplicant,
  UpdateLiabilitiesAction,
  UpsertLiabilitiesAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import {
  addDefaultOwners,
  getAssetId,
  getLiabilityId,
  removeClientSideOwnershipReference,
  sortByCreatedDateAsc,
  waitForAuth,
  waitForResourcesToBeCreated,
} from '../_common';
import RelationAPI from '../relations/api';
import {
  createLiability,
  createLiabilityFail,
  createLiabilitySuccess,
  deleteLiabilityFail,
  deleteLiabilitySuccess,
  fetchLiabilitiesFail,
  fetchLiabilitiesSuccess,
  setLiabilityError,
  updateLiability,
  updateLiabilityAbort,
  updateLiabilityFail,
  updateLiabilitySuccess,
  upsertLiabilitiesFail,
  upsertLiabilitiesSuccess,
} from './actions';
import LiabilityAPI from './api';
import moment from 'moment';
import { FIELD_VALUES } from '@funnels/constants';

export function* createLiabilityWorker(action: CreateLiabilityAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;
    const { clientId, ...liability } = action.liability;
    const response = yield call(LiabilityAPI.create, applicationId, liability);
    const liabilityId = response.data.id;
    const relations: AssetLiabilityRelation[] = yield createLiabilityAssetRelations(
      applicationId,
      action.liability,
      liabilityId
    );

    yield put(createLiabilitySuccess(clientId, liabilityId, relations.map(property('assetId'))));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      liability: { clientId },
    } = action;
    yield all([put(createLiabilityFail(clientId)), put(setLiabilityError(e.response?.data || e.message))]);
  }
}

export function* deleteLiabilityWorker(action: DeleteLiabilityAction): any {
  try {
    yield waitForAuth();

    const { applicationId, clientId, id } = action;
    const liabilityId = yield getLiabilityId(clientId, id);

    yield call(LiabilityAPI.del, applicationId, liabilityId);
    yield put(deleteLiabilitySuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield all([put(deleteLiabilityFail(clientId)), put(setLiabilityError(e.response?.data || e.message))]);
  }
}

export function* fetchLiabilitiesWorker(action: FetchLiabilitiesAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;
    const response = yield call(LiabilityAPI.fetch, applicationId);
    const appCreatedAt = yield select((state: ReduxStoreData) => state.applications?.data[0]?.createdAt);
    const liabilities = response.data.map((liability: any) => {
      if (
        liability.interestType === FIELD_VALUES.FIXED_RATE &&
        !liability.fixedTermExpiryDate &&
        liability.interestTypeDuration &&
        appCreatedAt
      ) {
        const calculatedDate = moment(appCreatedAt)
          .add(liability.interestTypeDuration * AVERAGE_DAYS_OF_A_YEAR, 'days')
          .format('YYYY-MM-01');
        liability.fixedTermExpiryDate = calculatedDate;
      }
      return omit(liability, ['meta', 'updatedAt']);
    });

    liabilities.sort(sortByCreatedDateAsc);

    yield put(fetchLiabilitiesSuccess(liabilities));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(fetchLiabilitiesFail()), put(setLiabilityError(e.response?.data || e.message))]);
  }
}

export function* updateLiabilityWorker(action: UpdateLiabilitiesAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;
    const liabilityId = yield getLiabilityId(action.liability.clientId, action.liability.id);
    const liability = omit(action.liability, [
      'clientId',
      'createdAt',
      'id',
      'isDeidentified',
      'isDeleted',
    ]) as ServerLiabilityUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!applicationId) {
      yield all([put(updateLiabilityAbort()), put(createLiability(applicationId, action.liability))]);
    } else {
      yield call(LiabilityAPI.update, applicationId, liabilityId, liability);

      const relations: AssetLiabilityRelation[] = yield createLiabilityAssetRelations(
        applicationId,
        action.liability,
        liabilityId
      );

      yield put(
        updateLiabilitySuccess(
          action.liability.clientId,
          relations.map(({ assetId }) => assetId)
        )
      );
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      liability: { clientId },
    } = action;
    yield all([put(updateLiabilityFail(clientId)), put(setLiabilityError(e.response?.data || e.message))]);
  }
}

function* createLiabilityAssetRelations(applicationId: string, liability: Liability, liabilityId: string): any {
  if (liability.assetClientId.length === 0) {
    return [];
  }

  const assets = yield select((state: ReduxStoreData) =>
    state.assets.data
      .filter((asset: Asset) => includes(liability.assetClientId, asset.clientId))
      .map((asset: Asset) => ({ id: asset.id, clientId: asset.clientId }))
  );
  const relations = [];

  for (let i = 0; i < assets.length; i++) {
    const asset = assets[i];
    const assetId = yield getAssetId(asset.clientId, asset.id);

    relations.push({ liabilityId, assetId });
  }

  yield call(RelationAPI.create, applicationId, {
    assetsAndLiabilities: relations,
  });

  return relations;
}

export function* upsertLiabilitiesWorker(action: UpsertLiabilitiesAction): any {
  try {
    yield waitForAuth();
    let reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);
    yield waitForResourcesToBeCreated({
      applicants: reduxStoreApplicants.map((a: StoreApplicant) => a.clientId),
    });
    reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);

    const { applicationId, liabilities } = action;
    const reduxStoreLiabilities: Liability[] = yield select((state: ReduxStoreData) => state.liabilities.data);

    // Filter out Id in the comparator function because its possible for that to be added by the store
    const changedLiabilities = differenceWith(
      liabilities,
      reduxStoreLiabilities,
      ({ id: id1, ...rest1 }, { id: id2, ...rest2 }) => isEqual(rest1, rest2)
    );

    const reduxStoreCustomer: CustomerData = yield select((state: ReduxStoreData) => state.customers?.data?.[0]);

    yield all(
      changedLiabilities.map((l) => {
        // We rely on grabbing the id from the store so that
        // we DON'T rely on the store syncing to the form after initialisation
        const reduxStoreLiability = find(reduxStoreLiabilities, { clientId: l.clientId });
        const liability = removeClientSideOwnershipReference(l, reduxStoreApplicants) as Liability;
        const liabilityWithDefaultOwners = addDefaultOwners(
          liability,
          reduxStoreApplicants,
          reduxStoreCustomer
        ) as Liability;
        if (!reduxStoreLiability) {
          // No liability
          return put(createLiability(applicationId, liabilityWithDefaultOwners));
        } else if (!reduxStoreLiability.id) {
          // Liability but no id, therefore create has been fired but success hasn't returned
          return put(updateLiability(applicationId, liabilityWithDefaultOwners));
        } else {
          // Asset is in the store and has an id
          return put(updateLiability(applicationId, { id: reduxStoreLiability.id, ...liabilityWithDefaultOwners }));
        }
      })
    );

    yield put(upsertLiabilitiesSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(upsertLiabilitiesFail()), put(setLiabilityError(e.response?.data || e.message))]);
  }
}

export function* liabilitiesWatcher() {
  yield takeEvery(REDUX_STORE.LIABILITIES.ACTION_TYPES.CREATE_LIABILITY, createLiabilityWorker);
  yield takeEvery(REDUX_STORE.LIABILITIES.ACTION_TYPES.DELETE_LIABILITY, deleteLiabilityWorker);
  yield takeEvery(REDUX_STORE.LIABILITIES.ACTION_TYPES.FETCH_LIABILITIES, fetchLiabilitiesWorker);
  yield takeEvery(REDUX_STORE.LIABILITIES.ACTION_TYPES.UPDATE_LIABILITY, updateLiabilityWorker);
  yield takeEvery(REDUX_STORE.LIABILITIES.ACTION_TYPES.UPSERT_LIABILITIES, upsertLiabilitiesWorker);
}

export default liabilitiesWatcher;
